import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import UserForm from '../../components/forms/UserForm';
import { getUser, createUser, updateUser } from '../../services/api';
import { UserFormData } from '../../types';

const UserFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const isEditMode = !!id;
  
  // Fetch user data if in edit mode
  useEffect(() => {
    const fetchUser = async () => {
      if (!isEditMode) return;
      
      setIsLoading(true);
      
      try {
        const data = await getUser(id);
        setUser(data);
      } catch (error) {
        console.error('Error fetching user:', error);
        setError('Failed to load user data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUser();
  }, [id, isEditMode]);
  
  // Handle form submission
  const handleSubmit = async (data: UserFormData) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      if (isEditMode) {
        // Update existing user
        await updateUser(id, data);
      } else {
        // Create new user
        await createUser(data);
      }
      
      navigate('/tenants?view=users');
    } catch (error: any) {
      console.error('Error saving user:', error);
      
      // Handle detailed validation errors from the backend
      if (error.response?.data) {
        if (error.response.data.details) {
          // Handle structured validation errors
          const validationErrors = Object.entries(error.response.data.details)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          setError(validationErrors);
        } else if (error.response.data.error) {
          // Handle specific error message
          setError(error.response.data.error);
        } else {
          setError('Failed to save user. Please try again.');
        }
      } else {
        setError('Failed to save user. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-b from-blue-900/90 via-indigo-900/70 to-transparent p-7 rounded-xl shadow-lg mb-6 border-t border-l border-r border-blue-700/30 relative overflow-hidden">
        {/* Subtle background pattern overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(99,102,241,0.1)_1px,transparent_1px)] bg-[length:24px_24px] opacity-20"></div>
        
        {/* Subtle accent line at top */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400/70 via-indigo-300/70 to-blue-400/70"></div>
        
        <div className="relative z-10 flex items-center">
          <Link to="/tenants?view=users">
            <Button variant="outline" className="mr-4 border border-blue-700/40 bg-blue-900/30 hover:bg-blue-800/40 text-blue-300" leftIcon={<ArrowLeft size={16} />}>
              Back
            </Button>
          </Link>
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-indigo-200 font-display tracking-wide">
            {isEditMode ? `Edit ${user?.username || 'User'}` : 'Add User'}
          </h1>
        </div>
      </div>
      
      {error && (
        <div className="bg-gradient-to-b from-red-900/30 to-transparent p-4 rounded-xl border-t border-l border-r border-red-800/30 shadow-md text-red-300">
          {error.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {line}
              {index < error.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
      )}
      
      <div className="bg-gradient-to-b from-gray-900/70 to-transparent p-6 rounded-xl backdrop-blur-sm border-t border-l border-r border-indigo-800/20 shadow-lg">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(99,102,241,0.05)_1px,transparent_1px)] bg-[length:24px_24px] opacity-20 rounded-xl pointer-events-none"></div>
        <div className="relative z-10">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-16 h-16 relative">
                <div className="absolute top-0 right-0 h-16 w-16 rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
                <div className="absolute top-1 right-1 h-14 w-14 rounded-full border-4 border-t-transparent border-r-indigo-500 border-b-transparent border-l-transparent animate-spin-slow"></div>
              </div>
            </div>
          ) : (
            <UserForm
              initialData={user}
              onSubmit={handleSubmit}
              isLoading={isSubmitting}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserFormPage; 